import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import styled, { StylesProps } from '../../lib/styles';
import { compose, withProps } from 'recompose';
import { Collection as CollectionModel } from '../../models/collection';
import memoize from '../../lib/selectorUtils';
import Ad, { Ads } from '../../components/presentational/Ad';

import NavigationBlock from './NavigationBlock';
import AuthorBlock from './AuthorBlock';
import Metadata from './Metadata';

import { withTracking } from '../../lib/ga';
import { SHOW_MORE_STORIES } from './lib/ga';

import { callOnMount } from '../../behaviour/patterns';
import { collectionSelector, adsSelector, Brand } from './store/selectors';
import {
  loadMoreArticlesThunk,
  loadPopularArticlesThunk,
} from './store/actions';
import { Block } from './store/reducer';

import collectionStyles from './Collection_styles';
import CollectionBlock from './CollectionBlock';
import AdSetup from '../../components/presentational/Ad/AdSetup';
import pick from 'lodash/pick';
import { ArticleBlockData } from './CollectionBlock/Blocks/ArticleBlock';
import { kebabCase } from 'lodash';
import StandardAdhesion from '../Article/StandardArticleLayout/StandardAdhesion';
import { COLLECTION_VIEW, raiseEvent } from '../../lib/gtm';
import { useLocalStorage } from '../../lib/hooks/useLocalStorage';
import dayjs from 'dayjs';
import { TrackingDataState } from '../../store/reducers/trackingData';

export interface CollectionProps extends CollectionModel {
  brand?: Brand;
  ads: Ads;
  loadMore: () => any;
  loadPopularArticles: (url: string) => any;
  blocks?: Block[];
  articleIds?: number[];
}

const articleMetadata = (blocks: Block[]) => {
  const articles = blocks.map(block => {
    switch (block.type) {
      case 'article_block':
        return block.articles;
      default:
        return [];
    }
  });
  return articles.length > 0 ? articles.reduce((a, b) => a.concat(b)) : [];
};

const hasPanelBlock = (blocks: Block[]) =>
  !!blocks.find(({ type }) => type === 'panel_block');

const hasTitleBlock = (blocks: Block[]) =>
  !!blocks.find(({ type }) => type === 'title_block');

const Collection = ({
  brand = 'sas',
  ads,
  // @ts-ignore
  slug,
  moreArticlesLoading,
  title,
  subtitle,
  link,
  breadcrumbs,
  links,
  popularArticles = [],
  loadMore,
  author,
  meetups = [],
  styles,
  hideAds,
  blocks = [],
  articleIds,
  // @ts-ignore
  section = 'Discover',
  page_type = 'page',
}: CollectionProps & StylesProps<typeof collectionStyles>) => {
  const [localStorageTracking, setLocalStorageTracking] = useLocalStorage('sas', null);
  const trackingData = useSelector((state: any): TrackingDataState => (state.trackingData));
  const isHomePage = page_type === 'homepage';

  useEffect(() => {
    // console.log('Collection useEffect', { trackingData, localStorageTracking, slug, page_type, isHomePage });
    if (!trackingData.isLoaded) return;
    setLocalStorageTracking({
      ...localStorageTracking,
      uuid: localStorageTracking ? localStorageTracking.uuid : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
      email: trackingData?.email,
      history: [
        ...(localStorageTracking ? localStorageTracking.history.filter((h: any) => h.timestamp > dayjs().subtract(1, 'week')) : []),
        ...[{ link, taxonomy: 'collection', id: null, brand, timestamp: dayjs().valueOf() }]
      ]
    });
  }, [trackingData]);
  

  useEffect(() => {
    // console.log('Collection', { hideAds, ads, });
    raiseEvent({
      ...COLLECTION_VIEW,
      ...{
        sasEventType: `COLLECTION_VIEW: ${title}`,
      },
    });
  }, []);

  return (
    <div>
      {ads.high_impact && (
        <div className={styles.highImpact}>
          <Ad
            ad={ads.high_impact}
            inline
            gutter={false}
            predefined
            backgroundColor={'transparent'}
          />
        </div>
      )}

      <AdSetup 
        predefineAds={pick(ads, ['high_impact', 'standard_adhesion'])} 
        pageLevelTargeting={ads && ads.standard_adhesion ?
          Object.entries(ads.standard_adhesion.targeting || {}).map(([key, value]) => ({ key, value }))
          .concat(
            link && page_type ? 
            [
              { key: 'page_path', value: new URL(link).pathname },
              { key: 'page_category', value: page_type },
            ] : []
          ) : 
          []
        }
      />

      <div className={`${styles.container} collection-container`}>
        <Metadata
          link={link}
          articles={[
            ...articleMetadata(blocks ? blocks : []),
            ...popularArticles,
          ]}
          breadcrumbs={breadcrumbs}
          homepage={isHomePage}
        />

        {!hasTitleBlock(blocks) && author && <AuthorBlock heading author={author} />}
        {!hasTitleBlock(blocks) && !hasPanelBlock(blocks) && title && (
          <NavigationBlock
            title={title}
            subtitle={subtitle}
            breadcrumbs={breadcrumbs}
            links={links}
          />
        )}

        {blocks.map((block, blockIndex) => {

          if (block.type == 'title_block') {
            return constructNavigationBlock(blocks, blockIndex, title, author, subtitle, breadcrumbs, links)
          } else {
            return <CollectionBlock
              key={blockIndex}
              block={block}
              ads={ads}
              blockIndex={blockIndex}
              author={author}
              brand={brand}
              popular={popularArticles}
              hideAds={hideAds}
              meetups={meetups}
              loadMore={loadMore}
              articleIds={articleIds}
              moreArticlesLoading={moreArticlesLoading}
            />
          }
        })}
      </div>

      {ads.standard_adhesion && !isHomePage && (
        <div className={styles.hiddenAd}>
          <StandardAdhesion
            ad={ads.standard_adhesion}
            section={kebabCase(section.toLowerCase())}
          />
        </div>
      )}
    </div>
  )
};

const constructNavigationBlock = (blocks, blockIndex, title, author, subtitle, breadcrumbs, links) => {
  return (
    <div key={blockIndex} className="title-block-container">
      {author && <AuthorBlock key={`author-${blockIndex}`} heading author={author} />}
      {!hasPanelBlock(blocks) && title && (
        <NavigationBlock
          title={title}
          key={`navigation-${blockIndex}`}
          subtitle={subtitle}
          breadcrumbs={breadcrumbs}
          links={links}
        />
      )}
    </div>
  )
}

const collectionPropsSelector = memoize(
  collectionSelector,
  adsSelector,
  (collection, ads) => ({ ...collection, ads })
);

const dispatchProps = dispatch => ({
  loadMore: withTracking(SHOW_MORE_STORIES, (url, blockIndex, articleIds) => {
    dispatch(loadMoreArticlesThunk(url, blockIndex, articleIds));
  }),
  loadPopularArticles: () => {
    dispatch(loadPopularArticlesThunk());
  },
});

const featureBlockExists = (blocks: Block[]) => {
  return blocks.filter((block: Block) => {
    return block.type === 'popular_block';
  }).length;
};

const withOnMount = compose(
  withProps(
    ({ blocks, loadPopularArticles, popularArticlesURL }: CollectionProps) => ({
      onMount: () => {
        if (
          popularArticlesURL &&
          (blocks === undefined || featureBlockExists(blocks))
        ) {
          loadPopularArticles(popularArticlesURL);
        }
      },
      articleIds:
        blocks &&
        blocks
          .filter(block => block.type === 'article_block')
          .map((block: ArticleBlockData) =>
            block.articles.map(article => article.id)
          )
          .reduce((acc, val) => acc.concat(val), []),
    })
  ),
  callOnMount('onMount')
);

export default compose(
  connect(collectionPropsSelector, dispatchProps),
  withOnMount,
  styled(collectionStyles)
)(Collection);
